import * as React from "react";
import { css } from "@emotion/core";
import { Position, Icon, Tooltip } from "@blueprintjs/core";
import {
  Table2,
  Column,
  Cell,
  ColumnHeaderCell,
  RenderMode,
} from "@blueprintjs/table";
import { useUnitActions, useUnitState, useSinkState } from "context/";
import { utcToZonedTime, format as formatInTimeZone } from "date-fns-tz";
import { isISO8601DateTime } from "../../utils/regex";

interface AnnotationTableProps {
  headers: string[];
  rows: { [key: string]: any }[];
}

const AnnotationTable = ({ headers, rows }: AnnotationTableProps) => {
  const { surveys, selectedSurveyId } = useSinkState();
  const { convert } = useUnitActions();
  const { unit, conversions } = useUnitState();

  const survey = surveys.find((survey) => survey.id === selectedSurveyId);

  const [sorting, setSorting] = React.useState({
    index: 0,
    ascending: true,
  });

  const sortedRows =
    sorting.index === null
      ? rows
      : [...rows].sort((a, b) => {
          const [first, second] = sorting.ascending
            ? [a[sorting.index].value, b[sorting.index].value]
            : [b[sorting.index].value, a[sorting.index].value];

          if (typeof first === "string") {
            return first < second ? -1 : first > second ? 1 : 0;
          }

          return first - second;
        });

  const defaultWidths =
    headers.length > 0 ? [100, ...new Array(headers.length - 1).fill(180)] : [];

  const [columnWidths, setColumnWidths] = React.useState(defaultWidths);

  React.useEffect(() => {
    setSorting({
      index: 0,
      ascending: true,
    });
  }, [headers]);

  const cellRenderer = (rowIndex: number, columnIndex: number) => {
    const cell = sortedRows[rowIndex][columnIndex];

    if (cell.unit && typeof cell.value === "number" && cell.convert !== false) {
      return <Cell>{convert(cell.value, cell.unit)}</Cell>;
    }

    const conversion = conversions.find(({ type }) => type === cell.unit);
    const displayUnit =
      conversion === undefined
        ? ""
        : unit === "sae"
        ? conversion.imperial_unit
        : conversion.metric_unit;

    if (isISO8601DateTime(cell.value)) {
      return (
        <Cell>
          {formatInTimeZone(
            utcToZonedTime(cell.value, survey.timezone),
            "yyyy-MM-dd HH:mm:ssXXX",
            {
              timeZone: survey.timezone,
            }
          )}
        </Cell>
      );
    }

    return (
      <Cell>
        {cell.value ? cell.value : cell.unit ? "Unknown" : ""}{" "}
        {(cell.value && displayUnit) || ""}
      </Cell>
    );
  };

  const headerCellRenderer = (columnIndex: number) => (
    <ColumnHeaderCell>
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.25rem 0.5rem;
        `}
        onClick={() => {
          if (sorting.index !== columnIndex) {
            setSorting((prev) => ({ ...prev, index: columnIndex }));
          } else {
            setSorting((prev) => ({
              ...prev,
              ascending: !prev.ascending,
            }));
          }
        }}
      >
        <span
          css={css`
            display: flex;
            align-items: center;
          `}
        >
          <span
            css={css`
              font-weight: 700;
            `}
          >
            {headers[columnIndex]}
          </span>
          {["UID", "RefID"].includes(headers[columnIndex]) && (
            <Tooltip
              position={Position.RIGHT}
              content={
                <div>
                  <h3>{headers[columnIndex]}</h3>
                  <p
                    css={css`
                      max-width: 45ch;
                    `}
                  >
                    {
                      {
                        UID: "A unique identifier is associated with a single measurement or markup.",
                        RefID:
                          "A report reference identifier is produced for measurements when reports are made. It can be used to help reference between the downloadable reports and the FYBR Cloud.",
                      }[headers[columnIndex] as "UID" | "RefID"]
                    }
                  </p>
                </div>
              }
            >
              <Icon
                icon="info-sign"
                size={16}
                css={css`
                  margin-left: 0.5rem;
                  opacity: 0.5;
                `}
              />
            </Tooltip>
          )}
        </span>
        {sorting.index === columnIndex && (
          <Icon icon={sorting.ascending ? "chevron-up" : "chevron-down"} />
        )}
      </div>
    </ColumnHeaderCell>
  );

  return (
    <Table2
      getCellClipboardData={(row: number, column: number) => {
        const cell = sortedRows[row][column];

        if (cell.unit && typeof cell.value === "number") {
          return convert(cell.value, cell.unit, false, false);
        }

        return sortedRows[row][column].value;
      }}
      numFrozenColumns={2}
      numRows={sortedRows.length}
      enableGhostCells
      renderMode={RenderMode.NONE}
      columnWidths={columnWidths}
      onColumnWidthChanged={(index, width) =>
        setColumnWidths((prev) => prev.map((v, i) => (index === i ? width : v)))
      }
      onCopy={() => null}
      cellRendererDependencies={[sorting]}
    >
      {headers.map((header) => (
        <Column
          key={header}
          columnHeaderCellRenderer={headerCellRenderer}
          cellRenderer={cellRenderer}
        />
      ))}
    </Table2>
  );
};

export default AnnotationTable;
